import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TrendingDown, Info } from 'lucide-react';
import axios from 'axios';
import { API } from '../utils/apiConfig';

/**
 * SavingsBadge — Phase C / P5
 * Renders a small green pill on the Orb match panel showing how many %
 * the passenger saves compared to the worst-tier 15 % service fee.
 *
 * Props:
 *   ridePrice    — number (EUR), the booked seat price
 *   distanceKm   — number, the booked-leg distance
 *   compact      — boolean, render as a tiny inline pill (default true)
 *
 * Calls /api/pricing/quote once on mount/change. Cached in module-level Map
 * so identical (price, km) tuples don't re-query.
 */
const _quoteCache = new Map();

const _key = (p, k) => `${Number(p).toFixed(2)}|${Math.round(k)}`;

async function fetchQuote(ridePrice, distanceKm) {
  const k = _key(ridePrice, distanceKm);
  if (_quoteCache.has(k)) return _quoteCache.get(k);
  try {
    const r = await axios.get(`${API}/pricing/quote`, {
      params: { ride_price: ridePrice, distance_km: distanceKm },
    });
    _quoteCache.set(k, r.data);
    return r.data;
  } catch {
    return null;
  }
}

export const SavingsBadge = ({ ridePrice, distanceKm, compact = true }) => {
  const { t } = useTranslation();
  const [quote, setQuote] = useState(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  useEffect(() => {
    if (!ridePrice || !distanceKm) return;
    let alive = true;
    fetchQuote(ridePrice, distanceKm).then((q) => { if (alive) setQuote(q); });
    return () => { alive = false; };
  }, [ridePrice, distanceKm]);

  if (!quote) return null;
  const saved = Number(quote.savings_pct || 0);
  if (saved <= 0) return null;     // ≤50km tier — no savings to advertise

  const pct = Math.round((quote.service_fee_percent || 0) * 100);
  const fee = Number(quote.service_fee || 0).toFixed(2);

  return (
    <span
      data-testid="orb-savings-badge"
      onMouseEnter={() => setTooltipOpen(true)}
      onMouseLeave={() => setTooltipOpen(false)}
      onClick={(e) => { e.stopPropagation(); setTooltipOpen((v) => !v); }}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        position: 'relative',
        padding: compact ? '3px 10px' : '6px 14px',
        borderRadius: 999,
        background: 'linear-gradient(135deg, rgba(34,197,94,0.20), rgba(22,163,74,0.20))',
        border: '1px solid rgba(34,197,94,0.45)',
        color: '#22c55e',
        fontSize: compact ? 11 : 13,
        fontWeight: 700,
        letterSpacing: 0.2,
        cursor: 'pointer',
        userSelect: 'none',
      }}
    >
      <TrendingDown size={compact ? 11 : 13} />
      −{saved}%
      <Info size={compact ? 10 : 12} style={{ opacity: 0.7 }} />
      {tooltipOpen && (
        <span
          data-testid="orb-savings-tooltip"
          style={{
            position: 'absolute', bottom: 'calc(100% + 6px)', left: '50%',
            transform: 'translateX(-50%)',
            background: '#0f172a',
            border: '1px solid rgba(34,197,94,0.35)',
            color: '#f1f5f9',
            borderRadius: 10, padding: '8px 12px',
            fontSize: 11, fontWeight: 500, lineHeight: 1.45,
            whiteSpace: 'nowrap', zIndex: 100,
            boxShadow: '0 8px 20px rgba(0,0,0,0.45)',
          }}
        >
          {t('pricing.tooltipLine1', { defaultValue: 'Servicegebühr {{pct}}% (zzgl. MwSt.)', pct })}
          <br />
          <span style={{ color: '#22c55e' }}>
            {t('pricing.tooltipLine2', { defaultValue: '{{fee}} € statt 15% — du sparst {{saved}}%', fee, saved })}
          </span>
        </span>
      )}
    </span>
  );
};

export default SavingsBadge;
